import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ModalService, UserService } from '@cfp/core';
import { BuildingPdfDownloadButton } from '@cfp/shared/building/pdf-download-button';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { Badge, Button, Icon } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { BuildingDetailsDTO, LabelGroupDTO } from 'csr-asset-app';
import { combineLatest, map } from 'rxjs';

@Component({
  selector: 'cfp-building-report-buttons',
  templateUrl: './building-report-buttons.html',
  imports: [Button, TranslateModule, Badge, Icon, BuildingPdfDownloadButton],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildingReportButtons {
  public readonly building = input.required<BuildingDetailsDTO | undefined>();
  public readonly labelGroups = input<LabelGroupDTO[]>();
  public readonly buildingPublicSharedId = input<string>();
  public readonly canDownloadReportFromInput = input<boolean>(false, { alias: 'canDownloadReport' });
  public readonly hasBuildingReportFeedback = input<boolean>(false);
  public readonly reportFileName = input<string | undefined>(undefined);

  public readonly shouldReload = output();
  public readonly showFeedback = output();

  private readonly labelGroupService = inject(LabelGroupService);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  protected readonly canShareReport = toSignal(
    combineLatest([
      this.userService.hasLicense('share_building_report'),
      toObservable(this.buildingPublicSharedId)
    ]).pipe(
      map(
        ([hasShareBuildingReportLicense, buildingPublicSharedId]) =>
          !buildingPublicSharedId && hasShareBuildingReportLicense
      )
    )
  );

  protected readonly canDownloadReport = toSignal(
    combineLatest([
      toObservable(this.canDownloadReportFromInput),
      this.userService.hasLicense('building_report_old'),
      this.userService.hasLicense('building_report')
    ]).pipe(
      map(
        ([canDownloadReportFromInput, hasBuildingReportOldLicense, hasBuildingReportLicense]) =>
          canDownloadReportFromInput || hasBuildingReportOldLicense || hasBuildingReportLicense
      )
    )
  );

  protected readonly canDownloadDumavaReport = toSignal(this.userService.hasLicense('dumava'));

  protected readonly labelGroup = computed(() => {
    const labelGroups = this.labelGroups();
    if (!labelGroups) {
      return undefined;
    }
    return this.labelGroupService.getDefaultLabelGroup(labelGroups);
  });

  protected readonly url = computed(() => {
    if (this.buildingPublicSharedId()) {
      return `/report/public/buildings/${this.buildingPublicSharedId()}`;
    }

    return `${this.router.url}`;
  });

  protected readonly fileName = computed(() => {
    const reportFileName = this.reportFileName();
    if (reportFileName) {
      return reportFileName;
    }

    return this.building()?.address?.display?.join('-').replace(/,/g, '').replace(/ /g, '-') ?? '';
  });

  protected async share(): Promise<void> {
    const { BuildingShareModal } = await import('@cfp/shared/building/share-modal');
    const bsModalRef = this.modalService.showSlideUpModal(BuildingShareModal, {
      buildingId: this.building()?.id
    });

    if (bsModalRef.content) {
      bsModalRef.content.onClose.subscribe((shouldReload: boolean) => {
        if (shouldReload) {
          this.shouldReload.emit();
        }
      });
    }
  }
}
